import React from 'react';
import {
  Container,
  Typography,
  Grid,
  CardContent,
  Box,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  TrendingUp,
  TrendingDown,
  AssignmentReturn as ReturnIcon,
  Assessment as ReportsIcon,
  People as ClientsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import { usePermissions } from '../../../contexts/PermissionContext';
import { GlassBackdrop, GlassSurface } from '../../../components/GlassDashboard';

interface StockModule {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  route: string;
  available: boolean;
  color: string;
  miniModuleName?: string | null;
}

const StockOverview: React.FC = () => {
  const navigate = useNavigate();
  const { alertState, showError, hideAlert } = useAlert();
  const { hasPermission } = usePermissions();

  const stockModules: StockModule[] = [
    {
      id: 'entries',
      title: 'Entrées de Stock',
      description: 'Gérer les entrées et réceptions',
      icon: <TrendingUp />,
      route: '/stock/entries',
      available: true,
      color: '#4CAF50',
      miniModuleName: 'Stock Entry',
    },
    {
      id: 'exits',
      title: 'Sorties de Stock',
      description: 'Gérer les sorties et expéditions',
      icon: <TrendingDown />,
      route: '/stock/exits',
      available: true,
      color: '#F44336',
      miniModuleName: 'Stock Exit',
    },
    {
      id: 'returns',
      title: 'Retours de Stock',
      description: 'Gérer les retours clients',
      icon: <ReturnIcon />,
      route: '/stock/returns',
      available: true,
      color: '#FF9800',
      miniModuleName: 'Stock Return',
    },
    {
      id: 'inventory',
      title: 'Inventaire',
      description: 'Consulter et gérer l\'inventaire',
      icon: <InventoryIcon />,
      route: '/stock/inventory',
      available: true,
      color: '#2196F3',
      miniModuleName: 'Inventory',
    },
    {
      id: 'clients',
      title: 'Suivi des Clients',
      description: 'Suivre les paiements des clients',
      icon: <ClientsIcon />,
      route: '/stock/clients',
      available: true,
      color: '#9C27B0',
      miniModuleName: 'Client Tracking',
    },
    {
      id: 'reports',
      title: 'Rapports Stock',
      description: 'Analyses et rapports détaillés',
      icon: <ReportsIcon />,
      route: '/stock/reports',
      available: false,
      color: '#FF9800',
      miniModuleName: null,
    },
  ];

  // Check if user has permission for a module
  const hasModulePermission = (module: StockModule) => {
    // Coming soon items are always disabled
    if (!module.available) return false;

    // Check permissions if miniModuleName is specified
    if (module.miniModuleName) {
      return hasPermission('Stock', 'view', module.miniModuleName);
    }

    return true;
  };

  // Map modules with access status
  const visibleModules = stockModules.map((module) => ({
    ...module,
    hasAccess: hasModulePermission(module),
  }));

  const handleModuleClick = (module: StockModule & { hasAccess: boolean }) => {
    if (module.hasAccess) {
      navigate(module.route);
    }
  };

  return (
    <Container maxWidth="xl" disableGutters sx={{ px: 0 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Tableau de bord du Stock
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Tableau de bord de la gestion de stock
        </Typography>
      </Box>


      {/* Stock Operations Modules */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Opérations de Stock
      </Typography>

      <GlassBackdrop>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {visibleModules.map((module) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={module.id}>
              <GlassSurface
                accentColor={module.hasAccess ? module.color : undefined}
                interactive={module.hasAccess}
                onClick={module.hasAccess ? () => handleModuleClick(module) : undefined}
                sx={{ p: 2, opacity: module.hasAccess ? 1 : 0.6 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    height: '100%',
                    minHeight: 180,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: module.hasAccess ? module.color : 'grey.400',
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    {React.cloneElement(module.icon, {
                      sx: { fontSize: 28, color: 'white' }
                    })}
                  </Avatar>

                  <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="h3"
                      color={module.hasAccess ? 'text.primary' : 'text.disabled'}
                      sx={{ fontSize: '1rem', fontWeight: 600 }}
                    >
                      {module.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={module.hasAccess ? 'text.secondary' : 'text.disabled'}
                      sx={{ minHeight: 40, fontSize: '0.875rem' }}
                    >
                      {module.description}
                    </Typography>
                    {!module.available && (
                      <Chip
                        label="Bientôt disponible"
                        size="small"
                        color="warning"
                        variant="filled"
                        sx={{ mt: 1, fontSize: '0.7rem' }}
                      />
                    )}
                    {module.available && !module.hasAccess && (
                      <Chip
                        label="Accès refusé"
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ mt: 1, fontSize: '0.7rem' }}
                      />
                    )}
                  </CardContent>
                </Box>
              </GlassSurface>
            </Grid>
          ))}
        </Grid>
      </GlassBackdrop>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Container>
  );
};

export default StockOverview;
